import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import '../../styles/modals/EventRsvpModal.css';

const EventRsvpModal = ({ isOpen, onClose, onSubmit, attendeeCount, event, isLoading }) => {
  const [primaryFirstName, setPrimaryFirstName] = useState('');
  const [primaryLastName, setPrimaryLastName] = useState('');
  const [additionalAttendees, setAdditionalAttendees] = useState([]);
  const [errors, setErrors] = useState({});

  // Initialize additional attendees array when attendeeCount changes or modal opens
  useEffect(() => {
    if (isOpen && attendeeCount > 1) {
      const newAdditionalAttendees = Array(attendeeCount - 1).fill('');
      setAdditionalAttendees(newAdditionalAttendees);
    } else if (isOpen && attendeeCount <= 1) {
      setAdditionalAttendees([]);
    }
  }, [attendeeCount, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPrimaryFirstName('');
      setPrimaryLastName('');
      setAdditionalAttendees([]);
      setErrors({});
    }
  }, [isOpen]);

  const handleAdditionalAttendeeChange = (index, value) => {
    const updated = [...additionalAttendees];
    updated[index] = value;
    setAdditionalAttendees(updated);
    
    // Clear error for this field when user starts typing
    if (errors[`additional_${index}`] && value.trim()) {
      const newErrors = { ...errors };
      delete newErrors[`additional_${index}`];
      setErrors(newErrors);
    }
  };

  const isFormValid = () => {
    // Check primary fields
    if (!primaryFirstName.trim() || !primaryLastName.trim()) {
      return false;
    }
    
    // Check additional attendees if required
    if (attendeeCount > 1) {
      for (let i = 0; i < additionalAttendees.length; i++) {
        if (!additionalAttendees[i].trim()) {
          return false;
        }
      }
    }
    
    return true;
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate primary attendee fields - always required
    if (!primaryFirstName.trim()) {
      newErrors.primaryFirstName = 'First name is required';
    }
    if (!primaryLastName.trim()) {
      newErrors.primaryLastName = 'Last name is required';
    }

    // Validate additional attendees - all are required if attendee count > 1
    if (attendeeCount > 1) {
      additionalAttendees.forEach((name, index) => {
        if (!name.trim()) {
          newErrors[`additional_${index}`] = 'This name is required';
        } else if (name.trim().length < 2) {
          newErrors[`additional_${index}`] = 'Name must be at least 2 characters';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Process additional attendee names (all should be filled since validation passed)
    const processedAdditionalAttendees = additionalAttendees
      .map(name => name.trim())
      .filter(name => name.length > 0);

    const rsvpData = {
      primary_attendee_first_name: primaryFirstName.trim(),
      primary_attendee_last_name: primaryLastName.trim(),
      attendee_names: processedAdditionalAttendees.length > 0 ? processedAdditionalAttendees : null,
      attendee_count: attendeeCount, // Use the original attendee count since all fields are required
      status: 'going'
    };

    onSubmit(rsvpData);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="rsvp-modal-overlay" onClick={handleOverlayClick}>
      <div className="rsvp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rsvp-modal-header">
          <h3>RSVP for {event?.title || 'Event'}</h3>
          <button className="rsvp-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="rsvp-modal-form">
          {Object.keys(errors).length > 0 && (
            <div className="validation-summary">
              <p>Please fill in all required fields to continue.</p>
            </div>
          )}
          
          <div className="rsvp-modal-content">
            <div className="attendee-section">
              <h4>Primary Attendee</h4>
              <div className="name-row">
                <div className="form-group">
                  <label htmlFor="primaryFirstName">First Name *</label>
                  <input
                    type="text"
                    id="primaryFirstName"
                    value={primaryFirstName}
                    onChange={(e) => {
                      setPrimaryFirstName(e.target.value);
                      // Clear error when user starts typing
                      if (errors.primaryFirstName && e.target.value.trim()) {
                        const newErrors = { ...errors };
                        delete newErrors.primaryFirstName;
                        setErrors(newErrors);
                      }
                    }}
                    className={errors.primaryFirstName ? 'error' : ''}
                    placeholder="Enter first name"
                    disabled={isLoading}
                    required
                  />
                  {errors.primaryFirstName && (
                    <span className="error-message">{errors.primaryFirstName}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="primaryLastName">Last Name *</label>
                  <input
                    type="text"
                    id="primaryLastName"
                    value={primaryLastName}
                    onChange={(e) => {
                      setPrimaryLastName(e.target.value);
                      // Clear error when user starts typing
                      if (errors.primaryLastName && e.target.value.trim()) {
                        const newErrors = { ...errors };
                        delete newErrors.primaryLastName;
                        setErrors(newErrors);
                      }
                    }}
                    className={errors.primaryLastName ? 'error' : ''}
                    placeholder="Enter last name"
                    disabled={isLoading}
                    required
                  />
                  {errors.primaryLastName && (
                    <span className="error-message">{errors.primaryLastName}</span>
                  )}
                </div>
              </div>
            </div>

            {attendeeCount > 1 && (
              <div className="attendee-section">
                <h4>Additional Attendees ({attendeeCount - 1})</h4>
                <p className="section-description">Enter the full names of additional people attending with you</p>
                {additionalAttendees.map((name, index) => (
                  <div key={index} className="form-group">
                    <label htmlFor={`additional_${index}`}>
                      Attendee {index + 2} Name *
                    </label>
                    <input
                      type="text"
                      id={`additional_${index}`}
                      value={name}
                      onChange={(e) => handleAdditionalAttendeeChange(index, e.target.value)}
                      className={errors[`additional_${index}`] ? 'error' : ''}
                      placeholder="Enter full name"
                      disabled={isLoading}
                      required
                    />
                    {errors[`additional_${index}`] && (
                      <span className="error-message">{errors[`additional_${index}`]}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rsvp-modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="btn-rsvp" disabled={isLoading || !isFormValid()}>
              {isLoading ? 'Submitting...' : 'RSVP Now'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EventRsvpModal;
